import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, User as UserIcon } from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/contexts/Auth0Context'
import { useToast } from '@/contexts/ToastContext'
import { profileService } from '@/services/profileService'
import { User } from '@/types/user'

const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const { showToast } = useToast()

  const [profileUser, setProfileUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!userId || userId === currentUser?.id) return

    let cancelled = false
    setIsLoading(true)
    setNotFound(false)

    profileService
      .getUserById(userId)
      .then((data) => {
        if (!cancelled) setProfileUser(data)
      })
      .catch((error) => {
        if (cancelled) return
        setNotFound(true)
        showToast({
          type: 'error',
          message: 'Unable to load profile',
          description: error instanceof Error ? error.message : 'Please try again later.',
        })
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [userId, currentUser?.id, showToast])

  if (!userId) {
    return <Navigate to="/" replace />
  }

  // Viewing your own profile goes to the editable page instead.
  if (userId === currentUser?.id) {
    return <Navigate to="/profile" replace />
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (notFound || !profileUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4">This profile could not be found.</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
          </CardHeader>
          <CardBody>
            <div className="flex items-center space-x-6 mb-6">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                {profileUser.profile?.profilePicture ? (
                  <img
                    src={profileUser.profile.profilePicture}
                    alt={`${profileUser.firstName} ${profileUser.lastName}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserIcon className="w-10 h-10 text-primary-600" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {profileUser.firstName} {profileUser.lastName}
                </h3>
                <p className="text-gray-500 capitalize">{profileUser.role}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <p className="text-gray-600 text-sm">{profileUser.profile?.bio || 'No bio added yet.'}</p>
              </div>

              {profileUser.role === 'mentor' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expertise Areas
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {profileUser.profile?.expertise?.length > 0 ? (
                      profileUser.profile.expertise.map((skill, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full"
                        >
                          {skill}
                        </span>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">No expertise areas added yet.</p>
                    )}
                  </div>
                </div>
              )}

              {profileUser.role === 'mentee' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Interests</label>
                    <div className="flex flex-wrap gap-2">
                      {profileUser.profile?.interests?.length > 0 ? (
                        profileUser.profile.interests.map((interest, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                          >
                            {interest}
                          </span>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No interests added yet.</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Learning Goals
                    </label>
                    <div className="space-y-1">
                      {profileUser.profile?.goals?.length > 0 ? (
                        profileUser.profile.goals.map((goal, index) => (
                          <p key={index} className="text-gray-600 text-sm">
                            • {goal}
                          </p>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No learning goals added yet.</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default UserProfilePage
